/**
 * @author Hiroki Moto
 */

import React from 'react';
import PropTypes from 'prop-types';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import cx from 'classnames';
import DatePicker from '@components/@core/DatePicker';
import ProgressBar from '@components/@core/ProgressBar';
import Rating from '@components/@core/Rating';

const moment = extendMoment(originalMoment);

const Ratings = ({ data, date, onDateChange }) => {
    const ratingsAvg = Math.round((data.stats?.rating_avg || 0) * 10) / 10;
    const ratingRecommended = Math.round((data.stats?.rating_recommended_avg || 0) * 10) / 10;
    return (
        <>
            <div className="card pt-1">
                <div className="d-h-end">
                    <DatePicker calendar="range" append="caret" format="MMM D, YY" as="span" align="right" value={date} mountEvent closeAfterSelect onSelect={onDateChange} />
                </div>
                <p className="dsl-b24 bold">Total All Products</p>
                <div className="d-flex ssm-column jc-between mt-3">
                    <div className="report-card">
                        <p className="dsl-b20 bold">Ratings</p>
                        <div className="d-flex">
                            <Rating score={ratingsAvg} size="medium" />
                            <span className="dsl-b20 text-400 ms-2">{ratingsAvg}</span>
                        </div>
                        <p className="dsl-b18 bold mt-2">{ratingRecommended}% Recommended</p>
                        <p className="dsl-b14 mt-4 mb-1">{data.stats?.rating_count} Verified Ratings</p>
                        <p className="dsl-b14 my-1">By {data.stats?.rating_dealership_count} Dealerships</p>
                        <p className="dsl-b14 my-1">NPS: {data.stats?.nps}</p>
                    </div>
                    <div className="report-card">
                        <p className="dsl-b20 bold">Star Rating Distribution</p>
                        {['5', '4', '3', '2', '1'].map((key, index) => (
                            <div className={cx('d-flex', index !== 0 && 'mt-2')} key={`sr${index}`}>
                                <Rating score={data?.ratings_breakdown ? data.ratings_breakdown[`${key}-stars`]?.stars : 0} />
                                <span className="dsl-b16 ms-4">{data?.ratings_breakdown ? data.ratings_breakdown[`${key}-stars`]?.count : 0}</span>
                            </div>
                        ))}
                    </div>
                    <div className="report-card mt-0 ssm-t-3">
                        <p className="dsl-b20 bold">Rating by Job Title</p>
                        {data.ratings_by_job &&
                            data.ratings_by_job.map((item, index) => (
                                <ProgressBar key={`pr${index}`} className={cx('rating-by', index !== 0 && 'mt-11')} title={item.job} value={item.percent} horizontal />
                            ))}
                    </div>
                </div>
            </div>

            {data.products.map((item, index) => {
                const itemRatingsAvg = Math.round((item.stats?.rating_avg || 0) * 10) / 10;
                const itemRatingRecommended = Math.round((item.stats?.rating_recommended_avg || 0) * 10) / 10;
                if (item.data?.sponsored_level !== 'gold' && item.data?.sponsored_level !== 'silver') return null;
                return (
                    <div className="card mt-3" key={`r${index}`}>
                        <div className="d-flex jc-between">
                            <span className="dsl-b24 bold">{item.name}</span>
                        </div>
                        <div className="d-flex ssm-column jc-between mt-3">
                            <div className="report-card">
                                <p className="dsl-b20 bold">Ratings</p>
                                <div className="d-flex">
                                    <Rating score={itemRatingsAvg} size="medium" />
                                    <span className="dsl-b20 text-400 ms-2">{itemRatingsAvg}</span>
                                </div>
                                <p className="dsl-b18 bold mt-2">{itemRatingRecommended}% Recommended</p>
                                <p className="dsl-b14 mt-4 mb-1">{item.stats?.rating_count} Verified Ratings</p>
                                <p className="dsl-b14 my-1">By {item.stats?.rating_dealership_count} Dealerships</p>
                                <p className="dsl-b14 my-1">NPS: {item.stats?.nps}</p>
                            </div>
                            <div className="report-card">
                                <p className="dsl-b20 bold">Star Rating Distribution</p>
                                {['5', '4', '3', '2', '1'].map((key, idx) => (
                                    <div className={cx('d-flex', idx !== 0 && 'mt-2')} key={`sr${index}${idx}`}>
                                        <Rating score={item?.ratings_breakdown[`${key}-stars`]?.stars} />
                                        <span className="dsl-b16 ms-4">{item?.ratings_breakdown[`${key}-stars`]?.count}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="report-card">
                                <p className="dsl-b20 bold">Rating by Job Title</p>
                                {item.ratings_by_job.map((progress, idx) => (
                                    <ProgressBar key={`pr${index}${idx}`} className={cx('rating-by', index !== 0 && 'mt-11')} title={progress.job} value={progress.percent} horizontal />
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

Ratings.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        products: PropTypes.array,
        stats: PropTypes.shape({
            product_count: PropTypes.number,
            popularity_score: PropTypes.number,
            rating_recommended_avg: PropTypes.number,
            rating_count: PropTypes.number,
            rating_dealership_count: PropTypes.number,
        }),
    }),
    date: PropTypes.any,
    onDateChange: PropTypes.func,
};

Ratings.defaultProps = {
    data: {
        id: 0,
        name: '',
        products: [],
        stats: {
            product_count: 0,
            popularity_score: 0,
            rating_recommended_avg: 0,
            rating_count: 0,
            rating_dealership_count: 0,
        },
    },
    date: moment.range(moment().startOf('month').format('YYYY-MM-DD'), moment().endOf('month').format('YYYY-MM-DD')),
    onDateChange: () => {},
};

export default Ratings;
